import { TrashIcon } from '@heroicons/react/24/outline'
import NamingPatternField from './NamingPatternField'
import { renderNamingPattern, intentToken } from '../utils/namingPattern'
import { SAMPLE_VALUES, type NameValues } from '../utils/naming'
import React from 'react'

/**
 * The reusable Intune app-assignment editor (016-template-model §11.5).
 *
 * Mirrors Apps -> <app> -> Properties -> Assignments: one section per assignment type, each row
 * carrying the per-assignment settings Intune actually exposes. `intents` selects which
 * sections appear, so the same component serves the main app object (all three intents) and the
 * update-only object (Required + Uninstall only — nobody browses the Company Portal for an
 * updater).
 *
 * Two rules are enforced here rather than left to the backend to reject:
 *
 *  - AUDIENCE MIX. Mixing user groups and device groups across include/exclude on ONE intent is
 *    NOT SUPPORTED by Microsoft: exclusion membership is not evaluated instantly, so devices can
 *    wrongly receive the assignment. The backend blocks it; surfacing it inline means an admin
 *    learns at the row rather than on save. An ASSIGNED group can hold either kind, so it is
 *    reported as unverifiable rather than treated as decisive.
 *  - DEADLINE IS REQUIRED-ONLY. Intune only honours an installation deadline on Required, so the
 *    control is not rendered elsewhere — showing a deadline that never fires is worse than none.
 */

export type Intent = 'required' | 'available' | 'uninstall'
export type TargetKind = 'allDevices' | 'allUsers' | 'group'
/** Entra's Membership type: how members get in. */
export type GroupMembershipType = 'assigned' | 'dynamic'
/** What the members are. `mixed` means it cannot be told — an assigned group holds either kind. */
export type GroupMemberType = 'device' | 'user' | 'mixed'

export interface AssignmentGroup {
  id: string
  name: string
  membershipType: GroupMembershipType
  memberType: GroupMemberType
}

/**
 * One Entra group as the API returns it, reduced to what the blade needs.
 *
 * The two axes stay separate: `membershipType` says Assigned or Dynamic, `memberType` says users or
 * devices. One field cannot carry both, and reading either for the other reports the opposite of
 * the truth. The fallbacks derive the same answers from `membershipRule` so a frontend running
 * ahead of the backend still reads the group correctly.
 */
export function toAssignmentGroup(g: {
  id: string
  displayName?: string | null
  name?: string | null
  membershipRule?: string | null
  membershipType?: string | null
  memberType?: string | null
}): AssignmentGroup {
  const rule = (g.membershipRule || '').toLowerCase()
  const derivedMember: GroupMemberType = !rule
    ? 'mixed'
    : rule.includes('device.') === rule.includes('user.')
      ? 'mixed'
      : rule.includes('device.')
        ? 'device'
        : 'user'
  return {
    id: g.id,
    name: g.displayName ?? g.name ?? '',
    membershipType: (g.membershipType as GroupMembershipType) ?? (rule ? 'dynamic' : 'assigned'),
    memberType: (g.memberType as GroupMemberType) ?? derivedMember,
  }
}

/** How a group reads in the picker: "Marketing — Assigned", "Windows laptops — Dynamic · devices". */
export function groupLabel(g: AssignmentGroup): string {
  const membership = g.membershipType === 'dynamic' ? 'Dynamic' : 'Assigned'
  const members =
    g.memberType === 'device' ? ' · devices' : g.memberType === 'user' ? ' · users' : ''
  return `${g.name} — ${membership}${members}`
}

export interface AssignmentRow {
  intent: Intent
  target: TargetKind
  groupId?: string | null
  groupSource?: 'existing' | 'generated'
  mode: 'include' | 'exclude'
  filter?: string | null
  filterMode?: 'include' | 'exclude'
  notifications: 'showAll' | 'onRestart' | 'hideAll'
  timeZone: 'utc' | 'device'
  availability: string
  deadline?: string | null
  restartGracePeriod: {
    enabled: boolean
    graceMinutes: number
    countdownMinutes: number
    allowSnooze: boolean
    snoozeMinutes: number
  }
  contentDownload: 'background' | 'foreground'
  /**
   * Carried through the round trip, not edited here.
   *
   * The blade renders three intents; Intune also has `availableWithoutEnrollment`, and Company
   * Portal auto-update of a superseded app is an assignment SETTING with no control. Both are set
   * by the publish path. Because saving REPLACES the whole assignment set, a value the blade
   * cannot represent is not merely hidden — it is deleted on the next save. So they ride along
   * verbatim and go back exactly as they came.
   */
  rawIntent?: string | null
  autoUpdateSupersededApps?: boolean
}

/**
 * A blank assignment row.
 *
 * `target` has NO default. Defaulting it to every device meant a template saved without opening
 * the assignment step published the app to the whole estate, so the caller must state who this
 * row is for.
 */
export const newAssignmentRow = (intent: Intent, target: TargetKind): AssignmentRow => ({
  intent,
  target,
  groupId: null,
  groupSource: 'existing',
  mode: 'include',
  filter: null,
  filterMode: 'include',
  notifications: 'showAll',
  timeZone: 'utc',
  availability: 'asap',
  // Required-only; left null elsewhere so the saved row never carries a deadline Intune ignores.
  deadline: intent === 'required' ? 'asap' : null,
  restartGracePeriod: {
    // Off, and NO control renders for it, because PSADT already owns the user-facing restart:
    // AEAM.Extensions.ps1 `Invoke-AEAMRestartPrompt` calls Show-ADTInstallationRestartPrompt with
    // -CountdownSeconds, or -NoCountdown when the countdown is 0 — which is the postpone path (the
    // prompt re-displays every Config.UI.RestartPromptPersistInterval, 600s). Intune's grace period
    // and its snooze are the SAME capability from the other owner, so exposing them here would
    // give an admin two places to configure one experience and let a device show both prompts.
    // Publish enforces the single owner by sending deviceRestartBehavior=suppress whenever the
    // template's restart prompt is on (see template_service.resolve_device_restart_behavior).
    enabled: false,
    graceMinutes: 1440,
    countdownMinutes: 15,
    allowSnooze: true,
    snoozeMinutes: 240,
  },
  contentDownload: 'background',
})

const INTENT_LABEL: Record<Intent, string> = {
  required: 'Required',
  available: 'Available for enrolled devices',
  uninstall: 'Uninstall',
}

/** Resolve a row's audience. Returns null when it genuinely cannot be determined — a group we
 *  don't know, or one whose members can be either kind. */
function rowAudience(row: AssignmentRow, groups: AssignmentGroup[]): 'device' | 'user' | null {
  if (row.target === 'allDevices') return 'device'
  if (row.target === 'allUsers') return 'user'
  if (row.groupSource === 'generated') {
    // Publish creates these: Required lands on a device group, Available on a user group
    // (§7a.3), so the audience is known even before the group exists.
    return row.intent === 'available' ? 'user' : 'device'
  }
  const g = groups.find((x) => x.id === row.groupId)
  if (!g || g.memberType === 'mixed') return null
  return g.memberType
}

/** The Microsoft rule, evaluated per intent. */
export function audienceProblem(
  rows: AssignmentRow[],
  groups: AssignmentGroup[],
  intent: Intent,
): string | null {
  const mine = rows.filter((r) => r.intent === intent)
  const seen = new Set<'device' | 'user'>()
  let unknown = false
  for (const r of mine) {
    const a = rowAudience(r, groups)
    if (a) seen.add(a)
    else unknown = true
  }
  // The limitation is about EXCLUSIONS specifically: excluding a user group from an assignment
  // that targets devices (or the reverse) is unsupported, because exclusion membership is not
  // evaluated instantly and a device can receive the app before the exclusion lands.
  //
  // Two INCLUDES of different audiences is legitimate and in production use: our own endpoint gate
  // assigns a device group AND all-users (filtered to the device) on the same app, so IME enforces
  // whichever principal it resolves on a workplace-joined machine. Flagging that as an error - which
  // this did - would have blocked a configuration the product itself depends on. Verified against
  // the live assignments on app 56509984.
  const hasExclude = mine.some((r) => r.mode === 'exclude')
  if (seen.has('device') && seen.has('user') && hasExclude) {
    return (
      'Mixes user and device audiences across an include and an exclude. Microsoft does not ' +
      'support this — exclusion membership is not evaluated instantly, so devices can wrongly ' +
      'receive it. Two includes of different audiences is fine.'
    )
  }
  if (unknown && mine.some((r) => r.mode === 'exclude')) {
    return (
      'An exclusion here targets a group whose audience cannot be confirmed. Make sure every ' +
      'include and exclude on this intent targets the same audience (all users, or all devices).'
    )
  }
  return null
}

/** The one constraint the mock states for this block: a snooze cannot outlast the grace period it
 *  is snoozing within. Surfaced as a warning rather than clamped, because silently rewriting a
 *  number an admin typed is worse than telling them it is wrong. */
export function snoozeProblem(row: AssignmentRow): string | null {
  const rgp = row.restartGracePeriod
  if (!rgp.enabled || !rgp.allowSnooze) return null
  if (rgp.snoozeMinutes > rgp.graceMinutes) {
    return `Cannot exceed the grace period (${rgp.graceMinutes} min).`
  }
  return null
}

/** The short form of each section, for summaries — the reference's `INTENT_TITLE`. */
export const INTENT_TITLE: Record<Intent, string> = {
  required: 'Required',
  available: 'Available',
  uninstall: 'Uninstall',
}

/** Renders the group name for one row, where the caller generates its groups. */
export type GeneratedGroupName = (row: AssignmentRow) => string

/**
 * How one row reads in a list: a group name, or Intune's virtual target.
 *
 * A GENERATED group has no id to look up, because publish is what creates it — so the caller
 * supplies the name its pattern resolves to. Falling back to the group list would report "—" for
 * a target that is perfectly well known.
 */
export function assignmentTargetLabel(
  row: AssignmentRow,
  groups: AssignmentGroup[],
  generated?: GeneratedGroupName,
): string {
  if (row.target === 'allDevices') return 'All Devices'
  if (row.target === 'allUsers') return 'All Users'
  if (row.groupSource === 'generated') return generated?.(row) ?? '—'
  return groups.find((g) => g.id === row.groupId)?.name ?? '—'
}

/** What Intune collides on: the TARGET of an INCLUDED assignment. Null for an exclusion — those
 *  do not collide — and for a group row with nothing chosen yet. */
function inclusionKey(row: AssignmentRow, generated?: GeneratedGroupName): string | null {
  if (row.mode === 'exclude') return null
  if (row.target !== 'group') return row.target
  if (row.groupSource === 'generated') {
    // Keyed on the NAME the pattern renders, not on the row: publish creates one group per
    // distinct name, so two generated rows on a pattern with no {Intent} land on one group.
    const name = generated?.(row)
    return name ? `generated:${name}` : null
  }
  return row.groupId ? `group:${row.groupId}` : null
}

/**
 * Targets included more than once on the same app object.
 *
 * Intune allows ONE included assignment per target on an app, whatever the intent. Graph refuses
 * the second one ("An inclusion intent already exists for group id <guid>") and rejects the WHOLE
 * assign call, so the deployment lands nothing at all. Checked across intents, because that is
 * where it happens: Available and Uninstall both left on All Devices.
 */
export function duplicateTargetProblems(
  rows: AssignmentRow[],
  groups: AssignmentGroup[],
  generated?: GeneratedGroupName,
): string[] {
  const byTarget = new Map<string, AssignmentRow[]>()
  for (const r of rows) {
    const key = inclusionKey(r, generated)
    if (!key) continue
    byTarget.set(key, [...(byTarget.get(key) ?? []), r])
  }
  return [...byTarget.values()]
    .filter((rs) => rs.length > 1)
    .map((rs) => {
      const intents = [...new Set(rs.map((r) => INTENT_TITLE[r.intent]))].join(' and ')
      return (
        `${assignmentTargetLabel(rs[0], groups, generated)} is included by ${rs.length} ` +
        `assignments (${intents}). Intune allows one included assignment per target on an app, ` +
        `whatever the intent, and rejects the whole set — so nothing is deployed. Keep one.`
      )
    })
}

/**
 * A blade summarised in Intune's own vocabulary, for list rows and review screens:
 * "Required: Ring 2 – Broad except Ring 0 · Uninstall: Developers".
 *
 * Sections come out in Intune's order, and one that assigns nothing is dropped rather than
 * printed empty.
 */
export function assignmentSummary(
  rows: AssignmentRow[],
  groups: AssignmentGroup[],
  generated?: GeneratedGroupName,
): string {
  if (!rows.length) return 'Not assigned'
  const label = (r: AssignmentRow) => assignmentTargetLabel(r, groups, generated)
  return (['required', 'available', 'uninstall'] as Intent[])
    .map((intent) => {
      const mine = rows.filter((r) => r.intent === intent)
      if (!mine.length) return null
      const inc = mine.filter((r) => r.mode !== 'exclude').map(label)
      const exc = mine.filter((r) => r.mode === 'exclude').map(label)
      const parts = [inc.join(', '), exc.length ? `except ${exc.join(', ')}` : ''].filter(Boolean)
      return `${INTENT_TITLE[intent]}: ${parts.join(' ')}`
    })
    .filter(Boolean)
    .join(' · ')
}

// ------------------------------------------------------------------ the review table
/** One assignment as a review screen reads it out — the columns of `AssignmentReviewTable`. */
export interface AssignmentReviewRow {
  /** Which Intune app object it belongs to: the app, or its update-only twin. */
  object: string
  intent: string
  target: string
  /** The group does not exist yet; publish is what creates it. */
  generated: boolean
  mode: string
  filter: string
  settings: string
}

/** An instant as a review reads it. A value that is not a date is printed as it stands, so a
 *  malformed one is visible rather than rendered "Invalid Date". */
function reviewInstant(iso: string): string {
  const at = new Date(iso)
  return isNaN(at.getTime())
    ? iso
    : at.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

/** The assignment filter by name, and the direction Intune applies it in. */
export function assignmentFilterLabel(
  row: AssignmentRow,
  filters: { id: string; name: string }[],
): string {
  if (!row.filter) return '—'
  const name = filters.find((f) => f.id === row.filter)?.name ?? row.filter
  return `${name} (${row.filterMode === 'exclude' ? 'exclude' : 'include'})`
}

/**
 * The row's own settings — everything in the blade's Settings disclosure — listed only where it
 * DIFFERS from `newAssignmentRow`.
 *
 * Printing all six on every row states the defaults over and over and buries the one an admin
 * changed, which is the only reason to read the column.
 */
export function assignmentSettingsSummary(row: AssignmentRow): string {
  const out: string[] = []
  if (row.notifications === 'onRestart') out.push('Notifications: restarts only')
  if (row.notifications === 'hideAll') out.push('Notifications: hidden')
  if (row.timeZone === 'device') out.push('Device time zone')
  if (row.availability && row.availability !== 'asap') {
    out.push(`Available ${reviewInstant(row.availability)}`)
  }
  // Required-only: Intune honours a deadline on no other intent, and the blade leaves it unset
  // there, so an absent one is the default rather than a choice.
  if (row.intent === 'required') {
    if (!row.deadline) out.push('No deadline')
    else if (row.deadline !== 'asap') out.push(`Deadline ${reviewInstant(row.deadline)}`)
  }
  if (row.restartGracePeriod?.enabled) {
    out.push(`Restart grace ${row.restartGracePeriod.graceMinutes} min`)
  }
  if (row.contentDownload === 'foreground') out.push('Download in foreground')
  return out.join(' · ') || 'Defaults'
}

/** Whether the tenant already has a group of this name. Publish matches generated groups by
 *  name and reuses one it finds, so a name that is already taken is not created again. */
function groupNamed(name: string | undefined, groups: AssignmentGroup[]): boolean {
  if (!name) return false
  const wanted = name.toLowerCase()
  return groups.some((g) => g.name.toLowerCase() === wanted)
}

/**
 * One app object's assignments, flattened for the review table in Intune's intent order.
 *
 * `generated` names the groups publish creates, for the reason `assignmentTargetLabel` takes it:
 * they have no id to look up until publish makes them. A generated name the tenant already has,
 * from an earlier deployment that was removed, is reused rather than created, so it is not new.
 */
export function assignmentReviewRows(
  object: string,
  rows: AssignmentRow[],
  groups: AssignmentGroup[],
  filters: { id: string; name: string }[],
  generated?: GeneratedGroupName,
): AssignmentReviewRow[] {
  return (['required', 'available', 'uninstall'] as Intent[]).flatMap((intent) =>
    rows
      .filter((r) => r.intent === intent)
      .map((r) => ({
        object,
        intent: INTENT_TITLE[intent],
        target:
          r.target === 'group' && r.groupSource !== 'generated' && !r.groupId
            ? 'No group chosen'
            : assignmentTargetLabel(r, groups, generated),
        generated:
          r.target === 'group' &&
          r.groupSource === 'generated' &&
          !groupNamed(generated?.(r), groups),
        mode: r.mode === 'exclude' ? 'Exclude' : 'Include',
        filter: assignmentFilterLabel(r, filters),
        settings: assignmentSettingsSummary(r),
      })),
  )
}

/** The ported data-table classes, tightened: this one sits inside a wizard section rather than a
 *  card, so it keeps the row height of the read-outs around it. */
const reviewTh = 'th px-0 pr-3 py-1.5 last:pr-0'
const reviewTd = 'td px-0 pr-3 py-1.5 align-top last:pr-0'

/**
 * Every assignment a review is about to publish, one per row.
 *
 * A line per INTENT ran the object, the target, the mode and the filter together in one sentence,
 * and a template that generates its groups repeated the resolved pattern in each — three names an
 * admin then had to compare inside a paragraph.
 */
export const AssignmentReviewTable: React.FC<{ rows: AssignmentReviewRow[] }> = ({ rows }) => {
  if (!rows.length) return <p className="text-sm text-theme-text-tertiary">Not assigned</p>
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-theme-border-primary">
            <th className={reviewTh}>Object</th>
            <th className={reviewTh}>Intent</th>
            <th className={reviewTh}>Target</th>
            <th className={reviewTh}>Mode</th>
            <th className={reviewTh}>Filter</th>
            <th className={reviewTh}>Settings</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-theme-border-primary last:border-0">
              <td className={`${reviewTd} text-theme-text-tertiary`}>{r.object}</td>
              <td className={reviewTd}>{r.intent}</td>
              <td className={`${reviewTd} font-mono text-[13px]`}>
                {r.target}
                {r.generated && (
                  <span className="ml-1.5 rounded border border-theme-border-primary px-1 font-sans text-[10px] uppercase text-theme-text-tertiary">
                    new
                  </span>
                )}
              </td>
              <td className={`${reviewTd} text-theme-text-secondary`}>{r.mode}</td>
              <td className={`${reviewTd} text-theme-text-secondary`}>{r.filter}</td>
              <td className={`${reviewTd} text-[12px] text-theme-text-tertiary`}>{r.settings}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** The reference's `AddBtn` — an outline button, not a borrowed field style. */
const addBtn =
  'rounded-lg border border-theme-border-primary px-2.5 py-1 text-xs font-medium text-theme-text-primary hover:bg-theme-tertiary'

const sel =
  'w-full px-2 py-1 bg-theme-primary border border-theme-border-primary rounded text-theme-text-primary text-xs'

/** One labelled cell of a row grid -- the reference's `<Field compact>`: label above control,
 *  never beside it. The row used to be a single wrapping strip, so a label could end up on a
 *  different line from the select it named. */
const Cell: React.FC<{ label: string; hint?: string; className?: string; children: React.ReactNode }> = ({
  label,
  hint,
  className = '',
  children,
}) => (
  <div className={className}>
    <span className="label">{label}</span>
    {hint && <p className="mb-1 text-[11px] text-theme-text-tertiary">{hint}</p>}
    {children}
  </div>
)

/** A row cell that reports rather than edits -- the reference's `ReadOnlyCell`. */
const ReadOnlyCell: React.FC<{ children: React.ReactNode; mono?: boolean; value?: boolean }> = ({
  children,
  mono,
  value,
}) => (
  <p
    className={`truncate rounded border border-dashed border-theme-border-primary px-2 py-1 text-xs ${
      value ? 'text-theme-text-primary' : 'text-theme-text-tertiary'
    } ${mono ? 'font-mono' : ''}`}
  >
    {children}
  </p>
)


/**
 * V-3 datetime plumbing. The row stores an ISO-8601 instant, because that is what
 * install_time_settings carries to Graph; <input type="datetime-local"> speaks local wall time
 * with no zone. These convert between the two rather than storing whatever the input hands back,
 * which would silently change meaning with the viewer's timezone.
 */
const toLocalInput = (iso: string): string => {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
}
/**
 * null when the input cannot be parsed — which includes the EMPTY string the control emits on
 * every keystroke while a date is being typed or cleared.
 *
 * This returned 'asap' in that case, and 'asap' is not a neutral value: the mapper omits
 * deadline_datetime for it (utils/assignmentPayload.ts:54), so Intune falls back to its own
 * deadline, which on a Required app means install now. Clearing the field therefore turned
 * "install by next Friday" into "install immediately", silently, mid-typing. The caller now
 * ignores null and keeps the value the row already had.
 */
const fromLocalInput = (v: string): string | null => {
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}
/** A sensible first value when switching to "at a specific time" — never a past instant. */
const isoLocalPlusDays = (days: number): string => {
  const d = new Date()
  d.setDate(d.getDate() + days)
  d.setSeconds(0, 0)
  return d.toISOString()
}

export interface AssignmentBladeProps {
  value: AssignmentRow[]
  onChange: (rows: AssignmentRow[]) => void
  /** Which sections to render. */
  intents: Intent[]
  groups?: AssignmentGroup[]
  /**
   * Offer "create from naming pattern" as a per-row group source (§6.2).
   *
   * This is a CAPABILITY of the caller, not a user setting: a caller that cannot create groups —
   * the per-app assignment modal, which posts rows against one existing app id — must not offer
   * the option at all, because there is no pattern to name the group and no publish step to
   * create it.
   */
  allowGeneratedGroups?: boolean
  disabled?: boolean
  /** Shown above the sections: which app object this blade targets, and how a row picks its
   *  group. The reference passes one per step, since the same blade serves both objects. */
  note?: React.ReactNode
  /** V-3: Intune assignment filters, so the per-row filter can actually be chosen. Optional —
   *  with none supplied the control says so instead of offering an empty select. */
  filters?: { id: string; name: string }[]
  /**
   * The name every group this blade creates is given. Rendered inside the blade, so each step
   * configures its own — the app object and the update-only object must never share a group, and
   * a pattern field living out on the wizard step left the update object's name unreachable.
   */
  groupPattern?: string
  onGroupPatternChange?: (v: string) => void
  onGroupPatternValidity?: (bad: boolean) => void
  /**
   * What {Intent} renders as in a generated group's name, when the app object forces a literal
   * instead of taking the row's own intent. The update-only object does: every one of its rows
   * resolves to ONE group (spec §6.2), so without this the blade both shows a name publish will
   * not create and misses the collision when two rows land on that one group.
   */
  groupIntentToken?: string
  /**
   * Token values for the group-name preview, when the blade is editing for a known app. Omitted,
   * the preview uses the sample app.
   */
  previewValues?: NameValues
}

const AssignmentBlade: React.FC<AssignmentBladeProps> = ({
  value,
  onChange,
  intents,
  groups = [],
  allowGeneratedGroups,
  disabled,
  filters = [],
  note,
  groupPattern,
  onGroupPatternChange,
  onGroupPatternValidity,
  groupIntentToken,
  previewValues,
}) => {
  const patch = (idx: number, next: Partial<AssignmentRow>) => {
    const rows = value.slice()
    rows[idx] = { ...rows[idx], ...next }
    // Keep the Required-only deadline invariant true no matter how the intent changed.
    if (next.intent && next.intent !== 'required') rows[idx].deadline = null
    // Intune takes no assignment filter on an exclusion, so a filter is cleared rather than kept
    // out of sight on a row that cannot send it.
    if (next.mode === 'exclude') rows[idx].filter = null
    onChange(rows)
  }
  // Which row has its settings open, at most one. A button and state rather than a details
  // element: its summary has to be the first child, so it could never share the footer line with
  // the delete control.
  const [openRow, setOpenRow] = React.useState<number | null>(null)
  const add = (intent: Intent, target: TargetKind) =>
    onChange([...value, newAssignmentRow(intent, target)])
  const remove = (idx: number) => onChange(value.filter((_, i) => i !== idx))

  /**
   * Whether any row has its group created for it.
   *
   * DERIVED, never stored. Group source is a per-assignment decision and the row's select is the
   * only control for it; a second switch above the rows modelled the same decision at template
   * level, so a row could be set to create a group while the switch said no group would be
   * created, with nothing defining which of the two publish should believe.
   */
  const createsGroups = value.some((r) => r.target === 'group' && r.groupSource === 'generated')

  // Spans the intents, so it sits above them rather than inside one: the pair that collides is
  // almost always on two different intents.
  const generatedName = (r: AssignmentRow) =>
    groupPattern
      ? renderNamingPattern(groupPattern, {
          ...previewValues,
          intent: groupIntentToken ?? intentToken(r.intent),
        })
      : ''
  const duplicates = duplicateTargetProblems(value, groups, generatedName)

  return (
    <div>
      {/* Says which app object this blade targets, before the rows themselves. */}
      {note && <div className="mb-4">{note}</div>}
      {/* The name for those groups, shown once a row asks for one — one pattern serves every row,
          so it belongs above them rather than repeated in each. */}
      {allowGeneratedGroups && createsGroups && groupPattern !== undefined && onGroupPatternChange && (
        <div className="mb-5 rounded-xl border border-theme-border-primary bg-theme-secondary p-4">
          <p className="mb-2.5 text-[12px] text-theme-text-tertiary">
            A row set to <span className="text-theme-text-secondary">Create a group for this app</span>{' '}
            has its security group created and assigned at publish, named from this pattern.
          </p>
          <NamingPatternField
            scope="group"
            label="Group name"
            hint="Used for every group this template creates. Groups outlive any single release, so release-specific tokens aren’t offered here."
            value={groupPattern}
            onChange={onGroupPatternChange}
            onValidityChange={onGroupPatternValidity}
            /* Sample {Intent} as the literal this object forces, so the preview is the name
               publish will actually create rather than one intent's worth of it. */
            previewValues={
              groupIntentToken || previewValues
                ? {
                    ...SAMPLE_VALUES,
                    ...previewValues,
                    ...(groupIntentToken ? { intent: groupIntentToken } : {}),
                  }
                : undefined
            }
          />
        </div>
      )}

      {duplicates.length > 0 && (
        <div className="mb-5 space-y-1.5 rounded-xl border border-red-500/40 bg-red-500/[0.07] px-4 py-3">
          {duplicates.map((m) => (
            <p key={m} className="text-xs text-red-400">
              {m}
            </p>
          ))}
        </div>
      )}

      {intents.map((intent) => {
        const rows = value.map((r, i) => ({ r, i })).filter(({ r }) => r.intent === intent)
        const problem = audienceProblem(value, groups, intent)
        return (
          <div key={intent} className="mb-5 rounded-xl border border-theme-border-primary bg-theme-primary">
            <div className="flex items-center justify-between gap-3 border-b border-theme-border-primary px-4 py-2.5">
              <p className="text-sm font-semibold text-theme-text-primary">
                {INTENT_LABEL[intent]}
              </p>
              <span className="text-xs text-theme-text-tertiary">
                {rows.length === 0
                  ? 'No assignments'
                  : `${rows.length} assignment${rows.length > 1 ? 's' : ''}`}
              </span>
            </div>
            <div className="space-y-2 p-3">
            {problem && <p className="text-xs text-red-400 mb-2">{problem}</p>}

            {rows.map(({ r, i }) => (
              <div
                key={i}
                className="rounded-lg border border-theme-border-primary bg-theme-secondary p-3"
              >
                <div
                  className={`mb-2 grid gap-3 ${
                    // Only split the line when Group source is on it; a lone GROUP select takes
                    // the full width, as the reference draws it.
                    r.target === 'group' && allowGeneratedGroups
                      ? 'sm:grid-cols-[minmax(0,19rem)_minmax(0,1fr)]'
                      : 'sm:grid-cols-1'
                  }`}
                >
                  {/* Only where a group IS the target and this blade can create one. A row aimed
                      at All users has no group to source, and offering "create a group" where
                      nothing creates it produced a row that published against no group at all. */}
                  {r.target === 'group' && allowGeneratedGroups && (
                  <Cell label="Group source">
                    <select
                      className={sel}
                      disabled={disabled}
                      value={r.groupSource ?? 'existing'}
                      onChange={(e) =>
                        patch(i, {
                          groupSource: e.target.value as 'existing' | 'generated',
                          // A generated group is created BY this row, so excluding it is
                          // self-contradictory — force include.
                          mode: e.target.value === 'generated' ? 'include' : r.mode,
                          groupId: e.target.value === 'generated' ? null : r.groupId,
                        })
                      }
                    >
                      <option value="existing">Existing group — shared</option>
                      <option value="generated">Create a group for this app</option>
                    </select>
                  </Cell>
                  )}
                  {r.target === 'group' && r.groupSource === 'generated' ? (
                    <Cell label="Group" hint="Created from the template's group pattern.">
                      <ReadOnlyCell mono value>
                        {generatedName(r) || '—'}
                      </ReadOnlyCell>
                    </Cell>
                  ) : (
                  <Cell label="Group">
                    <select
                      className={sel}
                      disabled={disabled}
                      value={r.target === 'group' ? `group:${r.groupId ?? ''}` : r.target}
                      onChange={(e) => {
                        const v = e.target.value
                        // One control for the whole audience, as the reference has it: the two
                        // virtual targets and every real group in a single list, so choosing a
                        // group is one decision rather than a target plus a group.
                        if (v.startsWith('group:')) patch(i, { target: 'group', groupId: v.slice(6) || null })
                        else patch(i, { target: v as TargetKind, groupId: null })
                      }}
                    >
                      <option value="allUsers">All users</option>
                      <option value="allDevices">All devices</option>
                      {r.target === 'group' && !r.groupId && <option value="group:">Select a group…</option>}
                      {groups.map((g) => (
                        <option key={g.id} value={`group:${g.id}`}>
                          {groupLabel(g)}
                        </option>
                      ))}
                    </select>
                  </Cell>
                  )}
                  {r.target === 'group' && r.groupSource === 'generated' && (
                    <span className="text-xs text-theme-text-tertiary">
                      name from the group pattern · created empty at publish
                    </span>
                  )}

                </div>

                <div className="mb-2 grid gap-3 sm:grid-cols-3">
                  <Cell label="Group mode">
                    <select
                      className={sel}
                      disabled={disabled || r.groupSource === 'generated'}
                      value={r.mode}
                      onChange={(e) => patch(i, { mode: e.target.value as 'include' | 'exclude' })}
                    >
                      <option value="include">Include</option>
                      <option value="exclude">Exclude</option>
                    </select>
                  </Cell>
                  <Cell label="Filter">
                    <select
                      className={sel}
                      disabled={disabled || r.mode === 'exclude'}
                      value={r.filter ?? ''}
                      onChange={(e) => patch(i, { filter: e.target.value || null })}
                    >
                      <option value="">None</option>
                      {filters.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name}
                        </option>
                      ))}
                    </select>
                  </Cell>
                  <Cell label="Filter mode">
                    {r.mode === 'exclude' ? (
                      <ReadOnlyCell>Not supported on exclusions</ReadOnlyCell>
                    ) : r.filter ? (
                      <select
                        className={sel}
                        disabled={disabled}
                        value={r.filterMode ?? 'include'}
                        onChange={(e) =>
                          patch(i, { filterMode: e.target.value as 'include' | 'exclude' })
                        }
                      >
                        <option value="include">Include</option>
                        <option value="exclude">Exclude</option>
                      </select>
                    ) : (
                      <ReadOnlyCell>No filter</ReadOnlyCell>
                    )}
                  </Cell>
                </div>

                {/* Per-row settings behind a disclosure, with the delete control on the same
                    line — the reference's row footer. Every row rendering nine always-open
                    controls buried the one thing that distinguishes rows from each other. */}
                <>
                  <div className="mt-2.5 flex items-center justify-between gap-3 border-t border-theme-border-primary pt-2.5">
                    {r.mode === 'exclude' ? (
                      <span className="text-xs text-theme-text-tertiary">
                        An excluded group takes no assignment settings.
                      </span>
                    ) : (
                    <button
                      type="button"
                      onClick={() => setOpenRow((o) => (o === i ? null : i))}
                      aria-expanded={openRow === i}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-400 hover:underline"
                    >
                      <span
                        className={`inline-block transition-transform ${
                          openRow === i ? 'rotate-90' : ''
                        }`}
                      >
                        ›
                      </span>
                      Settings
                    </button>
                    )}
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => remove(i)}
                      title="Remove assignment"
                      aria-label="Remove assignment"
                      className="rounded-md p-1.5 text-theme-text-tertiary transition-colors hover:bg-red-500/10 hover:text-red-500"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                  {r.mode !== 'exclude' && openRow === i && (
                  <div className="mt-2.5 grid gap-3 rounded-lg border border-theme-border-primary bg-theme-secondary p-3 sm:grid-cols-2">
                  <Cell label="End user notifications">
                    <select
                      className={sel}
                      disabled={disabled}
                      value={r.notifications}
                      onChange={(e) => patch(i, { notifications: e.target.value as AssignmentRow['notifications'] })}
                    >
                      <option value="showAll">Show all toast notifications</option>
                      <option value="onRestart">Show toast notifications for computer restarts</option>
                      <option value="hideAll">Hide all toast notifications</option>
                    </select>
                  </Cell>

                  <Cell label="Time zone">
                    <select
                      className={sel}
                      disabled={disabled}
                      value={r.timeZone}
                      onChange={(e) => patch(i, { timeZone: e.target.value as 'utc' | 'device' })}
                    >
                      <option value="utc">UTC</option>
                      <option value="device">Device time zone</option>
                    </select>
                  </Cell>

                  <Cell label="App availability">
                    <select
                      className={sel}
                      disabled={disabled}
                      value={r.availability === 'asap' || !r.availability ? 'asap' : 'at'}
                      onChange={(e) =>
                        patch(i, {
                          availability: e.target.value === 'asap' ? 'asap' : isoLocalPlusDays(1),
                        })
                      }
                    >
                      <option value="asap">As soon as possible</option>
                      <option value="at">A specific date and time</option>
                    </select>
                    {r.availability && r.availability !== 'asap' && (
                      <input
                        type="datetime-local"
                        className={sel + ' mt-2'}
                        disabled={disabled}
                        value={toLocalInput(r.availability)}
                        onChange={(e) => {
                          const iso = fromLocalInput(e.target.value)
                          if (iso) patch(i, { availability: iso })
                        }}
                      />
                    )}
                  </Cell>

                  {/* Deadline is Required-only: Intune does not honour it on the other intents. */}
                  {r.intent === 'required' ? (
                    <Cell label="App installation deadline">
                        <select
                          className={sel}
                          disabled={disabled}
                          value={
                            r.deadline == null || r.deadline === ''
                              ? ''
                              : r.deadline === 'asap'
                                ? 'asap'
                                : 'at'
                          }
                          onChange={(e) => {
                            const v = e.target.value
                            patch(i, {
                              deadline:
                                v === '' ? null : v === 'asap' ? 'asap' : isoLocalPlusDays(7),
                            })
                          }}
                        >
                          <option value="asap">As soon as possible</option>
                          <option value="at">A specific date and time</option>
                          <option value="">None</option>
                        </select>
                      {r.deadline != null && r.deadline !== '' && r.deadline !== 'asap' && (
                        <input
                          type="datetime-local"
                          className={sel + ' mt-2'}
                          disabled={disabled}
                          value={toLocalInput(r.deadline)}
                          onChange={(e) => {
                            const iso = fromLocalInput(e.target.value)
                            if (iso) patch(i, { deadline: iso })
                          }}
                        />
                      )}
                    </Cell>
                  ) : (
                    /* The slot is kept so the two columns stay aligned on a row that has no
                       deadline: Intune honours one on Required assignments only. */
                    <div />
                  )}

                {/* RESTART GRACE PERIOD (R-3). Rendered because the mock renders it, and left
                    DISABLED by default for the reason the mock gives: PSADT owns the user-facing
                    restart, so Intune's stays out of the way. That is only true now that
                    Invoke-AEAMRestartPrompt is actually wired into the native frontend (R-1) —
                    before that, nobody owned it.

                    Note what "disabled" means on the wire: no restartSettings are sent at all, so
                    INTUNE'S DEFAULTS apply. It is not "no restart prompt" and not "no snooze";
                    disabling snooze requires enabling this block and answering No below. */}
                <Cell
                  label="Restart grace period"
                  hint={
                    r.restartGracePeriod.enabled
                      ? 'Intune gives the user this much time before it restarts the device.'
                      : "Disabled: Intune's default restart settings apply. The end-user experience owns any restart prompt the user sees."
                  }
                >
                  <div>
                    <select
                      className={sel}
                      disabled={disabled}
                      value={r.restartGracePeriod.enabled ? 'enabled' : 'disabled'}
                      onChange={(e) =>
                        patch(i, {
                          restartGracePeriod: {
                            ...r.restartGracePeriod,
                            enabled: e.target.value === 'enabled',
                          },
                        })
                      }
                    >
                      <option value="disabled">Disabled</option>
                      <option value="enabled">Enabled</option>
                    </select>
                  </div>
                  {!r.restartGracePeriod.enabled && (
                    <p className="mt-1 text-[11px] text-theme-text-tertiary">
                      PSADT owns the user-facing restart, so Intune's stays out of the way. Leaving
                      this disabled sends no restart settings, which means Intune's own defaults
                      apply — not "no restart".
                    </p>
                  )}

                  {r.restartGracePeriod.enabled && (
                    <div className="mt-2 grid gap-3 rounded-lg border border-theme-border-primary bg-theme-primary p-3 sm:col-span-2 sm:grid-cols-2">
                      <Cell label="Device restart grace period (minutes)">
                        <input
                          type="number"
                          min={0}
                          max={20160}
                          disabled={disabled}
                          value={r.restartGracePeriod.graceMinutes}
                          onChange={(e) =>
                            patch(i, {
                              restartGracePeriod: {
                                ...r.restartGracePeriod,
                                graceMinutes: Number(e.target.value),
                              },
                            })
                          }
                          className={sel + ' mt-0.5 w-full'}
                        />
                      </Cell>
                      <Cell label="Select when to display the restart countdown dialog box before the restart occurs (minutes)">
                        <input
                          type="number"
                          min={0}
                          max={1440}
                          disabled={disabled}
                          value={r.restartGracePeriod.countdownMinutes}
                          onChange={(e) =>
                            patch(i, {
                              restartGracePeriod: {
                                ...r.restartGracePeriod,
                                countdownMinutes: Number(e.target.value),
                              },
                            })
                          }
                          className={sel + ' mt-0.5 w-full'}
                        />
                      </Cell>
                      <Cell label="Allow user to snooze the restart notification">
                        <select
                          className={sel + ' mt-0.5 w-full'}
                          disabled={disabled}
                          value={r.restartGracePeriod.allowSnooze ? 'yes' : 'no'}
                          onChange={(e) =>
                            patch(i, {
                              restartGracePeriod: {
                                ...r.restartGracePeriod,
                                allowSnooze: e.target.value === 'yes',
                              },
                            })
                          }
                        >
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </Cell>
                      {r.restartGracePeriod.allowSnooze && (
                        <Cell
                          label="Select the snooze duration (minutes)"
                          hint="Can’t exceed the device restart grace period."
                        >
                          <input
                            type="number"
                            min={0}
                            max={1440}
                            disabled={disabled}
                            value={r.restartGracePeriod.snoozeMinutes}
                            onChange={(e) =>
                              patch(i, {
                                restartGracePeriod: {
                                  ...r.restartGracePeriod,
                                  snoozeMinutes: Number(e.target.value),
                                },
                              })
                            }
                            className={sel + ' mt-0.5 w-full'}
                          />
                          {snoozeProblem(r) && (
                            <span className="mt-0.5 block text-[11px] text-amber-400">
                              {snoozeProblem(r)}
                            </span>
                          )}
                        </Cell>
                      )}
                    </div>
                  )}
                </Cell>
                  <Cell label="Delivery optimization priority">
                    <select
                      className={sel}
                      disabled={disabled}
                      value={r.contentDownload}
                      onChange={(e) =>
                        patch(i, { contentDownload: e.target.value as 'background' | 'foreground' })
                      }
                    >
                      <option value="background">Content download in background</option>
                      <option value="foreground">Content download in foreground</option>
                    </select>
                  </Cell>
                  </div>
                  )}
                </>
              </div>
            ))}
            {!disabled && (
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => add(intent, 'group')} className={addBtn}>
                  + Add group
                </button>
                <button type="button" onClick={() => add(intent, 'allUsers')} className={addBtn}>
                  + Add all users
                </button>
                <button type="button" onClick={() => add(intent, 'allDevices')} className={addBtn}>
                  + Add all devices
                </button>
              </div>
            )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default AssignmentBlade
